// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Check from "@awc/XDL/Icons/Check";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import color from "@awc/XDL/Utilities/color";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 12px;
        min-width: 12px;
        width: 12px;
        border-color: ${color("common.ui4")};
        border-radius: 1px;
        background: ${color("common.ui1")};
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 5px;
            line-height: ${fontConfig.lineHeight.singleLinexSmall};
        }
        &:focus-visible {
            outline: 1px solid ${color("primary")};
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("common.ui9")};
            border-color: ${color("common.ui9")};
            color: ${color("common.ui1")};
            justify-content: center;
        }
    }
`;
const hoverStyle = css`
    &:hover {
        ${CheckboxInputSelect} {
            ${CheckboxCheckedSelector} {
                background: ${color("common.ui8")};
                border-color: ${color("common.ui8")};
            }
            ${CheckboxDefaultSelector} {
                border-color: ${color("common.ui8")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector} {
            background: ${color("common.ui2", 0.33)};
            border: 1px solid ${color("common.ui4", 0.33)};
            cursor: not-allowed;
            justify-content: center;
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.regular,
            color: "primary.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLinexSmall};
                user-select: none;
            `,
        },
        iconProps: {
            src: Check,
            size: 13,
            ...({ height: 7, width: 9 } as any),
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            &&&[disabled] {
                cursor: not-allowed;
            }

            ${({ _labelPosition }: any) =>
                _labelPosition === "left" &&
                css`
                    margin-left: 0;
                `}

            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.medium,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                &:first-child {
                    margin-bottom: 12px;
                }
                &:empty {
                    display: none;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 12px;
            }
        `,
    },
} as xBaseTheme["checkbox"];
